import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, message, Typography, Space, Divider, Switch, Row, Col, Upload, Spin, Tooltip, Alert } from 'antd';
import { UserOutlined, MailOutlined, LockOutlined, SaveOutlined, PhoneOutlined, EnvironmentOutlined, GlobalOutlined, UploadOutlined, QuestionCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import userService from '../../services/userService';
import DashboardLayout from '../../components/Dashboard/DashboardLayout';
import UserBadges from '../../components/Common/UserBadges';
import { useDispatch, useSelector } from 'react-redux';
import { updateUserProfile } from '../../redux/userSlice';
import './Profile.css';
import env from '../../env';

const { Title, Text } = Typography;

// Password validation function
const validatePassword = (password) => {
  const hasMinLength = password.length >= 8;
  const hasCapitalLetter = /[A-Z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
  
  if (!hasMinLength) return 'Password must be at least 8 characters long';
  if (!hasCapitalLetter) return 'Password must contain at least one capital letter';
  if (!hasNumber) return 'Password must contain at least one number (0-9)';
  if (!hasSpecialChar) return 'Password must contain at least one special character';
  
  return null;
};

const Profile = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [profileImage, setProfileImage] = useState(null);
    const [profileImageFileList, setProfileImageFileList] = useState([]);
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user.currentUser);

    useEffect(() => {
        if (user) {
            form.setFieldsValue({
                first_name: user.first_name,
                last_name: user.last_name,
                email: user.email,
                phone_number: user.phone_number,
                location: user.location,
                bio: user.bio,
                website: user.website,
            });
            if (user.profile_image) {
                const profileImageUrl = env.BACKEND_STORAGE_URL + '/' + user.profile_image;
                setProfileImage(profileImageUrl);
                setProfileImageFileList([{
                    uid: '-1',
                    name: 'profile.jpg',
                    status: 'done',
                    url: profileImageUrl,
                }]);
            } else {
                // Clear image if no profile image exists
                setProfileImage(null);
                setProfileImageFileList([]);
            }
        }
    }, [user, form]);

    const handleImageUpload = (info) => {
        let fileList = [...info.fileList];
        fileList = fileList.slice(-1); // Only keep the last file
        fileList = fileList.map(file => {
            if (file.response) {
                file.url = file.response.url;
            }
            return file;
        });
        setProfileImageFileList(fileList);
    };

    const handleImageRemove = async (file) => {
        try {
            // If there's an existing profile image, delete it from backend
            if (user?.profile_image) {
                const response = await userService.deleteProfileImage(user.id);
                
                // Update Redux store with the response from backend
                if (response && response.user) {
                    dispatch(updateUserProfile(response.user));
                } else {
                    // Fallback: manually update if response structure is different
                    const updatedUser = {
                        ...user,
                        profile_image: null,
                        profile_image_url: null
                    };
                    dispatch(updateUserProfile(updatedUser));
                }
                
                message.success('Profile image deleted successfully');
            }
            
            // Clear local state
            setProfileImageFileList([]);
            setProfileImage(null);
        } catch (error) {
            console.error('Error deleting profile image:', error);
            message.error(error.response?.data?.message || 'Failed to delete profile image');
            // Don't remove from file list if deletion failed
            return false;
        }
        return true;
    };

    const handleSubmit = async (values) => {
        try {
            setLoading(true);
            
            // Get profile image file if uploaded
            const profileImageFile = profileImageFileList.length > 0 && profileImageFileList[0].originFileObj 
                ? profileImageFileList[0].originFileObj 
                : null;

            // Prepare form data
            const formData = { ...values };
            
            if (profileImageFile) {
                formData.profile_image = profileImageFile;
            }
            
            const response = await userService.updateUser(user.id, formData);
            
            // Debug: Log the response structure
            console.log('Response from userService:', response);
            console.log('Response.user:', response.user);
            
            // Check if we have the expected structure
            if (!response || !response.user) {
                console.error('Unexpected response structure:', response);
                message.error('Invalid response from server');
                return;
            }
            
            // Update Redux store with new user data
            const updatedUser = {
                ...user,
                ...response.user
            };
            
            // Debug: Log the updated user
            console.log('Updated user data:', updatedUser);
            
            dispatch(updateUserProfile(updatedUser));
            
            // Update form fields with new values immediately
            form.setFieldsValue({
                first_name: updatedUser.first_name,
                last_name: updatedUser.last_name,
                email: updatedUser.email,
                phone_number: updatedUser.phone_number,
                location: updatedUser.location,
                bio: updatedUser.bio,
                website: updatedUser.website,
            });
            
            // Update profile image file list if profile image was updated
            const profileImageUrl =  updatedUser.profile_image;
            if (profileImageUrl) {
                setProfileImage(profileImageUrl);
                setProfileImageFileList([{
                    uid: '-1',
                    name: 'profile.jpg',
                    status: 'done',
                    url: profileImageUrl,
                }]);
            }
            
            message.success('Profile updated successfully');
        } catch (error) {
            if (error.response?.data?.errors) {
                // Handle validation errors
                const errors = error.response.data.errors;
                Object.keys(errors).forEach(key => {
                    message.error(`${key.charAt(0).toUpperCase() + key.slice(1)}: ${errors[key][0]}`);
                });
            } else if (error.response?.data?.message) {
                message.error(error.response.data.message);
            } else {
                message.error('Failed to update profile');
            }
        } finally {
            setLoading(false);
        }
    };

    if (!user) {
        return (
            <DashboardLayout>
                <div style={{ marginBottom: 32 }}>
                    <Title level={2} style={{ margin: 0, color: '#1a1a1a', fontWeight: 600 }}>
                        👤 Profile Settings
                    </Title>
                    <Typography.Paragraph style={{ color: '#595959', fontSize: '16px', marginTop: 8, marginBottom: 0 }}>
                        Manage your account information and preferences. Keep your profile up to date!
                    </Typography.Paragraph>
                </div>
                <Spin spinning={true}>
                    <Typography.Text>Loading user profile...</Typography.Text>
                </Spin>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div style={{ marginBottom: 32 }}>
                <Title level={2} style={{ margin: 0, color: '#1a1a1a', fontWeight: 600 }}>
                    👤 Profile Settings
                </Title>
                <Typography.Paragraph style={{ color: '#595959', fontSize: '16px', marginTop: 8, marginBottom: 0 }}>
                    Manage your account information and preferences. Keep your profile up to date!
                </Typography.Paragraph>
            </div>

            <Spin spinning={loading}>
                <Form
                    id="profile-form"
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={user}
                >
                    {/* Personal Information */}
                    <Card 
                        className="form-section"
                        style={{ 
                            marginBottom: 32,
                            borderRadius: '16px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                            border: '1px solid #e8f4f8',
                            transition: 'all 0.3s ease',
                            overflow: 'hidden'
                        }}
                        bodyStyle={{ padding: '32px' }}
                    >
                        <div style={{ 
                            borderBottom: '2px solid #f0f7ff',
                            paddingBottom: 20,
                            marginBottom: 28,
                            background: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 100%)',
                            margin: '-32px -32px 28px -32px',
                            padding: '24px 32px'
                        }}>
                            <Space align="center" size="middle">
                                <div style={{
                                    background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                    padding: '12px',
                                    borderRadius: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)'
                                }}>
                                    <UserOutlined style={{ color: 'white', fontSize: '24px' }} />
                                </div>
                                <div>
                                    <Title level={4} style={{ margin: 0, color: '#1a1a1a', fontWeight: 600 }}>
                                        Personal Information
                                    </Title>
                                    <Text type="secondary" style={{ fontSize: '15px', color: '#595959', display: 'block', marginTop: 4 }}>
                                        Your basic account details
                                    </Text>
                                </div>
                            </Space>
                        </div>
                        <Row gutter={16}>
                            <Col xs={24} sm={12}>
                                <Form.Item
                                    name="first_name"
                                    label={
                                        <Space size="small">
                                            <span style={{ fontWeight: 500, fontSize: '15px' }}>First Name</span>
                                            <Tooltip title="Your first name as it appears on your account">
                                                <QuestionCircleOutlined style={{ color: '#bfbfbf', fontSize: '14px', cursor: 'help' }} />
                                            </Tooltip>
                                        </Space>
                                    }
                                    rules={[{ required: true, message: 'Please enter your first name' }]}
                                >
                                    <Input 
                                        prefix={<UserOutlined style={{ color: '#1890ff' }} />} 
                                        placeholder="Enter your first name"
                                        size="large"
                                        style={{ borderRadius: '8px' }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item
                                    name="last_name"
                                    label={
                                        <Space size="small">
                                            <span style={{ fontWeight: 500, fontSize: '15px' }}>Last Name</span>
                                            <Tooltip title="Your last name as it appears on your account">
                                                <QuestionCircleOutlined style={{ color: '#bfbfbf', fontSize: '14px', cursor: 'help' }} />
                                            </Tooltip>
                                        </Space>
                                    }
                                    rules={[{ required: true, message: 'Please enter your last name' }]}
                                >
                                    <Input 
                                        prefix={<UserOutlined style={{ color: '#1890ff' }} />} 
                                        placeholder="Enter your last name"
                                        size="large"
                                        style={{ borderRadius: '8px' }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item
                            name="email"
                            label={
                                <Space size="small">
                                    <span style={{ fontWeight: 500, fontSize: '15px' }}>Email Address</span>
                                    <Tooltip title="Your email address is used for account notifications and login">
                                        <QuestionCircleOutlined style={{ color: '#bfbfbf', fontSize: '14px', cursor: 'help' }} />
                                    </Tooltip>
                                </Space>
                            }
                            rules={[
                                { required: true, message: 'Please enter your email address' },
                                { type: 'email', message: 'Please enter a valid email address' }
                            ]}
                            extra={
                                <Text type="secondary" style={{ fontSize: '13px' }}>
                                    📧 This email will be used for account notifications and login
                                </Text>
                            }
                        >
                            <Input 
                                prefix={<MailOutlined style={{ color: '#1890ff' }} />} 
                                placeholder="your.email@example.com"
                                size="large"
                                style={{ borderRadius: '8px' }}
                            />
                        </Form.Item>

                        {/* User Badges */}
                        {(user?.is_verified || user?.seller_trust_badge || user?.buyer_protection_plus) && (
                            <Form.Item 
                                label={
                                    <Space size="small">
                                        <span style={{ fontWeight: 500, fontSize: '15px' }}>Account Badges</span>
                                        <Tooltip title="These badges show your account verification status and trust level">
                                            <QuestionCircleOutlined style={{ color: '#bfbfbf', fontSize: '14px', cursor: 'help' }} />
                                        </Tooltip>
                                    </Space>
                                }
                            >
                                <div style={{ 
                                    padding: '16px',
                                    background: '#f0f7ff',
                                    borderRadius: '8px',
                                    border: '1px solid #e6f7ff'
                                }}>
                                    <UserBadges user={user} showLabels={true} iconSize={18} />
                                </div>
                            </Form.Item>
                        )}
                    </Card>

                    <Divider style={{ margin: '40px 0', borderColor: '#e8f4f8' }} />

                    {/* Contact Information */}
                    <Card 
                        className="form-section"
                        style={{ 
                            marginBottom: 32,
                            borderRadius: '16px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                            border: '1px solid #e8f4f8',
                            transition: 'all 0.3s ease',
                            overflow: 'hidden'
                        }}
                        bodyStyle={{ padding: '32px' }}
                    >
                        <div style={{ 
                            borderBottom: '2px solid #f6ffed',
                            paddingBottom: 20,
                            marginBottom: 28,
                            background: 'linear-gradient(135deg, #f6ffed 0%, #ffffff 100%)',
                            margin: '-32px -32px 28px -32px',
                            padding: '24px 32px'
                        }}>
                            <Space align="center" size="middle">
                                <div style={{
                                    background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                                    padding: '12px',
                                    borderRadius: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 4px 12px rgba(82, 196, 26, 0.3)'
                                }}>
                                    <PhoneOutlined style={{ color: 'white', fontSize: '24px' }} />
                                </div>
                                <div>
                                    <Title level={4} style={{ margin: 0, color: '#1a1a1a', fontWeight: 600 }}>
                                        Contact Information
                                    </Title>
                                    <Text type="secondary" style={{ fontSize: '15px', color: '#595959', display: 'block', marginTop: 4 }}>
                                        How others can reach you
                                    </Text>
                                </div>
                            </Space>
                        </div>
                        <Form.Item
                            name="phone_number"
                            label={
                                <Space size="small">
                                    <span style={{ fontWeight: 500, fontSize: '15px' }}>Phone Number</span>
                                    <Tooltip title="Your phone number helps bidders contact you directly">
                                        <QuestionCircleOutlined style={{ color: '#bfbfbf', fontSize: '14px', cursor: 'help' }} />
                                    </Tooltip>
                                </Space>
                            }
                            rules={[
                                { required: true, message: 'Please enter your phone number' },
                                { max: 20, message: 'Phone number must be less than 20 characters' },
                                { pattern: /^[\+]?[0-9\s\-\(\)]*$/, message: 'Please enter a valid phone number' }
                            ]}
                            extra={
                                <Text type="secondary" style={{ fontSize: '13px' }}>
                                    📞 Include country code (e.g., +92 300 1234567)
                                </Text>
                            }
                        >
                            <Input 
                                prefix={<PhoneOutlined style={{ color: '#52c41a' }} />} 
                                placeholder="+92 300 1234567"
                                size="large"
                                style={{ borderRadius: '8px' }}
                            />
                        </Form.Item>

                        <Form.Item
                            name="location"
                            label={
                                <Space size="small">
                                    <span style={{ fontWeight: 500, fontSize: '15px' }}>Location</span>
                                    <Tooltip title="Your city and country helps others know where you're located">
                                        <QuestionCircleOutlined style={{ color: '#bfbfbf', fontSize: '14px', cursor: 'help' }} />
                                    </Tooltip>
                                </Space>
                            }
                            extra={
                                <Text type="secondary" style={{ fontSize: '13px' }}>
                                    📍 Share your location to help bidders find you
                                </Text>
                            }
                        >
                            <Input 
                                prefix={<EnvironmentOutlined style={{ color: '#52c41a' }} />} 
                                placeholder="e.g., Karachi, Pakistan"
                                size="large"
                                style={{ borderRadius: '8px' }}
                            />
                        </Form.Item>

                        <Form.Item
                            name="website"
                            label={
                                <Space size="small">
                                    <span style={{ fontWeight: 500, fontSize: '15px' }}>Website</span>
                                    <Tooltip title="Link to your personal or business website (optional)">
                                        <QuestionCircleOutlined style={{ color: '#bfbfbf', fontSize: '14px', cursor: 'help' }} />
                                    </Tooltip>
                                </Space>
                            }
                            rules={[
                                { type: 'url', message: 'Please enter a valid URL' }
                            ]}
                            extra={
                                <Text type="secondary" style={{ fontSize: '13px' }}>
                                    🌐 Share your website or portfolio (optional)
                                </Text>
                            }
                        >
                            <Input 
                                prefix={<GlobalOutlined style={{ color: '#52c41a' }} />} 
                                placeholder="https://your-website.com"
                                size="large"
                                style={{ borderRadius: '8px' }}
                            />
                        </Form.Item>
                    </Card>

                    <Divider style={{ margin: '40px 0', borderColor: '#e8f4f8' }} />

                    {/* Profile Details */}
                    <Card 
                        className="form-section"
                        style={{ 
                            marginBottom: 32,
                            borderRadius: '16px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                            border: '1px solid #e8f4f8',
                            transition: 'all 0.3s ease',
                            overflow: 'hidden'
                        }}
                        bodyStyle={{ padding: '32px' }}
                    >
                        <div style={{ 
                            borderBottom: '2px solid #f0f7ff',
                            paddingBottom: 20,
                            marginBottom: 28,
                            background: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 100%)',
                            margin: '-32px -32px 28px -32px',
                            padding: '24px 32px'
                        }}>
                            <Space align="center" size="middle">
                                <div style={{
                                    background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                    padding: '12px',
                                    borderRadius: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)'
                                }}>
                                    <UserOutlined style={{ color: 'white', fontSize: '24px' }} />
                                </div>
                                <div>
                                    <Title level={4} style={{ margin: 0, color: '#1a1a1a', fontWeight: 600 }}>
                                        Profile Details
                                    </Title>
                                    <Text type="secondary" style={{ fontSize: '15px', color: '#595959', display: 'block', marginTop: 4 }}>
                                        Tell others about yourself
                                    </Text>
                                </div>
                            </Space>
                        </div>
                        <Form.Item
                            name="bio"
                            label={
                                <Space size="small">
                                    <span style={{ fontWeight: 500, fontSize: '15px' }}>About Me</span>
                                    <Tooltip title="Share a brief description about yourself. This helps others get to know you better!">
                                        <QuestionCircleOutlined style={{ color: '#bfbfbf', fontSize: '14px', cursor: 'help' }} />
                                    </Tooltip>
                                </Space>
                            }
                            extra={
                                <Text type="secondary" style={{ fontSize: '13px' }}>
                                    ✍️ Share a bit about yourself, your interests, or your business
                                </Text>
                            }
                        >
                            <Input.TextArea 
                                rows={5} 
                                placeholder="Tell us about yourself... Share your interests, experience, or what makes you unique!"
                                maxLength={500}
                                showCount
                                size="large"
                                style={{ borderRadius: '8px' }}
                            />
                        </Form.Item>

                        <Form.Item 
                            label={
                                <Space size="small">
                                    <span style={{ fontWeight: 500, fontSize: '15px' }}>Profile Picture</span>
                                    <Tooltip title="Upload a professional profile picture. JPG or PNG format, maximum 2 MB">
                                        <QuestionCircleOutlined style={{ color: '#bfbfbf', fontSize: '14px', cursor: 'help' }} />
                                    </Tooltip>
                                </Space>
                            }
                            extra={
                                <Text type="secondary" style={{ fontSize: '13px' }}>
                                    📷 Upload a clear profile picture (JPG/PNG, ≤ 2 MB). A good photo helps build trust!
                                </Text>
                            }
                        >
                            <Upload
                                listType="picture-card"
                                fileList={profileImageFileList}
                                onChange={handleImageUpload}
                                onRemove={handleImageRemove}
                                beforeUpload={() => false} // Prevent auto upload
                                maxCount={1}
                                accept="image/jpeg,image/png,image/jpg"
                                style={{ borderRadius: '8px' }}
                            >
                                {profileImageFileList.length < 1 && (
                                    <div style={{ textAlign: 'center' }}>
                                        <UploadOutlined style={{ fontSize: '32px', color: '#1890ff' }} />
                                        <div style={{ marginTop: 8, color: '#595959', fontWeight: 500 }}>Upload Profile Picture</div>
                                    </div>
                                )}
                            </Upload>
                        </Form.Item>
                    </Card>

                    <Divider style={{ margin: '40px 0', borderColor: '#e8f4f8' }} />

                    {/* Password Settings */}
                    <Card 
                        className="form-section"
                        style={{ 
                            marginBottom: 32,
                            borderRadius: '16px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                            border: '1px solid #e8f4f8',
                            transition: 'all 0.3s ease',
                            overflow: 'hidden'
                        }}
                        bodyStyle={{ padding: '32px' }}
                    >
                        <div style={{ 
                            borderBottom: '2px solid #fff7e6',
                            paddingBottom: 20,
                            marginBottom: 28,
                            background: 'linear-gradient(135deg, #fff7e6 0%, #ffffff 100%)',
                            margin: '-32px -32px 28px -32px',
                            padding: '24px 32px'
                        }}>
                            <Space align="center" size="middle">
                                <div style={{
                                    background: 'linear-gradient(135deg, #faad14 0%, #d48806 100%)',
                                    padding: '12px',
                                    borderRadius: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 4px 12px rgba(250, 173, 20, 0.3)'
                                }}>
                                    <LockOutlined style={{ color: 'white', fontSize: '24px' }} />
                                </div>
                                <div>
                                    <Title level={4} style={{ margin: 0, color: '#1a1a1a', fontWeight: 600 }}>
                                        Password Settings
                                    </Title>
                                    <Text type="secondary" style={{ fontSize: '15px', color: '#595959', display: 'block', marginTop: 4 }}>
                                        Keep your account secure with a strong password
                                    </Text>
                                </div>
                            </Space>
                        </div>
                        <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            padding: '16px',
                            background: '#fff7e6',
                            borderRadius: '8px',
                            border: '1px solid #ffe58f',
                            marginBottom: 24
                        }}>
                            <LockOutlined style={{ fontSize: '18px', color: '#faad14', marginRight: 12 }} />
                            <Switch 
                                checked={showPassword} 
                                onChange={setShowPassword}
                                style={{ marginRight: 12 }}
                            />
                            <span style={{ fontWeight: 500, fontSize: '15px', color: '#595959' }}>
                                Change Password
                            </span>
                            <Tooltip title="Toggle this switch to update your password">
                                <QuestionCircleOutlined style={{ color: '#bfbfbf', fontSize: '14px', cursor: 'help', marginLeft: 8 }} />
                            </Tooltip>
                        </div>
                        {showPassword && (
                            <>
                                <Alert
                                    message={<span style={{ fontWeight: 500 }}>🔒 Password Requirements</span>}
                                    description={
                                        <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                                            For your security, your password must meet these requirements:
                                            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                                                <li>At least 8 characters long</li>
                                                <li>One capital letter (A-Z)</li>
                                                <li>One number (0-9)</li>
                                                <li>One special character (!@#$%^&*...)</li>
                                            </ul>
                                        </div>
                                    }
                                    type="info"
                                    showIcon
                                    icon={<InfoCircleOutlined />}
                                    style={{ 
                                        marginBottom: 24,
                                        borderRadius: '8px',
                                        border: '1px solid #91d5ff'
                                    }}
                                />
                                <Form.Item
                                    name="password"
                                    label={
                                        <Space size="small">
                                            <span style={{ fontWeight: 500, fontSize: '15px' }}>New Password</span>
                                            <Tooltip title="Enter your new password. Make sure it meets all the requirements above.">
                                                <QuestionCircleOutlined style={{ color: '#bfbfbf', fontSize: '14px', cursor: 'help' }} />
                                            </Tooltip>
                                        </Space>
                                    }
                                    rules={[
                                        { min: 8, message: 'Password must be at least 8 characters long' },
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                if (!value) return Promise.resolve();
                                                const error = validatePassword(value);
                                                if (error) {
                                                    return Promise.reject(new Error(error));
                                                }
                                                return Promise.resolve();
                                            },
                                        }),
                                    ]}
                                >
                                    <Input.Password 
                                        prefix={<LockOutlined style={{ color: '#faad14' }} />} 
                                        placeholder="Enter your new password"
                                        size="large"
                                        style={{ borderRadius: '8px' }}
                                    />
                                </Form.Item>
                                <Form.Item
                                    name="password_confirmation"
                                    label={
                                        <Space size="small">
                                            <span style={{ fontWeight: 500, fontSize: '15px' }}>Confirm New Password</span>
                                            <Tooltip title="Re-enter your new password to confirm it">
                                                <QuestionCircleOutlined style={{ color: '#bfbfbf', fontSize: '14px', cursor: 'help' }} />
                                            </Tooltip>
                                        </Space>
                                    }
                                    dependencies={['password']}
                                    rules={[
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                if (!value || getFieldValue('password') === value) {
                                                    return Promise.resolve();
                                                }
                                                return Promise.reject(new Error('The two passwords do not match'));
                                            },
                                        }),
                                    ]}
                                    extra={
                                        <Text type="secondary" style={{ fontSize: '13px' }}>
                                            🔐 Re-enter your password to confirm
                                        </Text>
                                    }
                                >
                                    <Input.Password 
                                        prefix={<LockOutlined style={{ color: '#faad14' }} />} 
                                        placeholder="Confirm your new password"
                                        size="large"
                                        style={{ borderRadius: '8px' }}
                                    />
                                </Form.Item>
                            </>
                        )}
                    </Card>

                    <Divider style={{ margin: '40px 0', borderColor: '#e8f4f8' }} />

                    <div style={{ 
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 16,
                        paddingTop: 24
                    }}>
                        <div>
                            <Typography.Text type="secondary" style={{ fontSize: '14px' }}>
                                💡 Review your changes and click "Update Profile" when everything looks good!
                            </Typography.Text>
                        </div>
                        <Button
                            type="primary"
                            size="large"
                            htmlType="submit"
                            loading={loading}
                            icon={<SaveOutlined />}
                            style={{ 
                                minWidth: 180,
                                height: '44px',
                                borderRadius: '8px',
                                fontWeight: 500,
                                boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)'
                            }}
                        >
                            {loading ? 'Updating...' : '✅ Update Profile'}
                        </Button>
                    </div>
                </Form>
            </Spin>
        </DashboardLayout>
    );
};

export default Profile; 